import React from 'react';
import { Box, Grid, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  InfoOutlined as InfoIcon,
  TrendingDown as DownIcon,
  TrendingFlat as FlatIcon,
  TrendingUp as UpIcon,
} from '@mui/icons-material';
import type { ShareLinkAnalyticsResponse } from '../../../services/api';
import { formatInt, formatPercent, ratio, ShareVizPalette } from './shareLinksAnalyticsModel';
import { overlineSx, shareCardSx } from './shareLinksUi';

type Delta =
  | { kind: 'count'; current: number; previous: number }
  | { kind: 'rate'; current: number | null; previous: number | null };

/** Signed change vs the previous period, with an icon so direction never rides on colour alone. */
const DeltaBadge: React.FC<{ delta: Delta; periodLabel: string }> = ({ delta, periodLabel }) => {
  const theme = useTheme();
  let change: number | null = null;
  let text: string;
  if (delta.kind === 'count') {
    if (delta.previous === 0) {
      text = delta.current === 0 ? 'No change' : 'New this period';
    } else {
      change = (delta.current - delta.previous) / delta.previous;
      text = `${change >= 0 ? '+' : '−'}${Math.abs(change * 100).toFixed(Math.abs(change) >= 0.1 ? 0 : 1)}%`;
    }
  } else if (delta.current === null || delta.previous === null) {
    text = 'No baseline';
  } else {
    change = delta.current - delta.previous;
    text = `${change >= 0 ? '+' : '−'}${Math.abs(change * 100).toFixed(1)} pts`;
  }
  const up = change !== null && change > 0.0005;
  const down = change !== null && change < -0.0005;
  const color = up ? theme.palette.success.main : down ? theme.palette.error.main : 'rgba(var(--color-text-rgb), 0.55)';
  const Icon = up ? UpIcon : down ? DownIcon : FlatIcon;
  return (
    <Tooltip title={`Compared with ${periodLabel}`}>
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color }}>
        {change !== null ? <Icon sx={{ fontSize: 16 }} /> : null}
        <Typography variant="caption" sx={{ fontWeight: 700, color: 'inherit' }}>{text}</Typography>
      </Box>
    </Tooltip>
  );
};

/** 12-point-or-more trend line; decorative context, the exact values live in the trend charts. */
const Sparkline: React.FC<{ values: number[]; color: string }> = ({ values, color }) => {
  if (values.length < 2 || values.every((v) => v === 0)) return <Box sx={{ height: 32 }} />;
  const width = 120;
  const height = 32;
  const max = Math.max(...values);
  const step = width / (values.length - 1);
  const points = values.map((v, i) => [i * step, height - 2 - (max > 0 ? (v / max) * (height - 4) : 0)]);
  const line = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const area = `${line} L${width},${height} L0,${height} Z`;
  const [lastX, lastY] = points[points.length - 1];
  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" aria-hidden>
      <path d={area} fill={color} opacity={0.1} />
      <path d={line} fill="none" stroke={color} strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
      <circle cx={lastX} cy={lastY} r={2.5} fill={color} vectorEffect="non-scaling-stroke" />
    </svg>
  );
};

interface KpiTileProps {
  label: string;
  value: string;
  caption: React.ReactNode;
  info?: string;
  delta?: Delta;
  periodLabel: string;
  spark?: { values: number[]; color: string };
  accent?: string;
}

const KpiTile: React.FC<KpiTileProps> = ({ label, value, caption, info, delta, periodLabel, spark, accent }) => (
  <Box
    sx={{
      ...shareCardSx,
      p: 2,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      gap: 0.5,
      position: 'relative',
      overflow: 'hidden',
      ...(accent ? { '&::before': { content: '""', position: 'absolute', inset: '0 auto 0 0', width: 3, bgcolor: accent } } : {}),
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Typography sx={overlineSx}>{label}</Typography>
      {info ? (
        <Tooltip title={info}>
          <InfoIcon sx={{ fontSize: 14, color: 'rgba(var(--color-text-rgb), 0.45)' }} />
        </Tooltip>
      ) : null}
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
      <Typography sx={{ fontSize: '1.9rem', fontWeight: 700, lineHeight: 1.15, color: 'var(--color-text-strong)' }}>{value}</Typography>
      {delta ? <DeltaBadge delta={delta} periodLabel={periodLabel} /> : null}
    </Box>
    <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.62)', minHeight: 18 }}>{caption}</Typography>
    {spark ? <Box sx={{ mt: 'auto', pt: 0.5 }}><Sparkline values={spark.values} color={spark.color} /></Box> : null}
  </Box>
);

const ShareLinksKpiRow: React.FC<{
  data: ShareLinkAnalyticsResponse;
  palette: ShareVizPalette;
  periodLabel: string;
  /** Leave out "Projects shared" — with one project in scope it is always 1. */
  hideProjects?: boolean;
  /** Agents on the team in scope; adds the share of them sharing to "Agents sharing". */
  teamSize?: number;
}> = ({ data, palette, periodLabel, hideProjects = false, teamSize }) => {
  const { totals, previousTotals, series } = data;
  const completion = ratio(totals.verificationsCompleted, totals.verificationsStarted);
  const previousCompletion = ratio(previousTotals.verificationsCompleted, previousTotals.verificationsStarted);
  const linksPerSharer = ratio(totals.links, totals.sharers);
  const opensPerLink = ratio(totals.opens, totals.links);
  const topProject = data.projects[0];
  const teamShare = teamSize !== undefined ? ratio(totals.sharers, teamSize) : null;
  const linksPerSharerText = linksPerSharer !== null ? `${linksPerSharer.toFixed(1)} links per agent` : '—';

  const tiles: KpiTileProps[] = [
    {
      label: 'Links shared',
      value: formatInt(totals.links),
      delta: { kind: 'count', current: totals.links, previous: previousTotals.links },
      caption: totals.revokedLinks > 0 ? `${formatInt(totals.revokedLinks)} since revoked` : 'None revoked',
      spark: { values: series.map((p) => p.links), color: palette.links },
      accent: palette.links,
      periodLabel,
    },
    {
      label: 'Leads received',
      value: formatInt(totals.leads),
      delta: { kind: 'count', current: totals.leads, previous: previousTotals.leads },
      caption: `from ${formatInt(totals.uniqueLeadMobiles)} unique mobile${totals.uniqueLeadMobiles === 1 ? '' : 's'}`,
      info: 'Buyers who verified their mobile on WhatsApp and were filed into the agent\'s Super Connector, by verification date. Includes leads on links shared before this period.',
      spark: { values: series.map((p) => p.leads), color: palette.leads },
      accent: palette.leads,
      periodLabel,
    },
    {
      label: 'Agents sharing',
      value: formatInt(totals.sharers),
      delta: { kind: 'count', current: totals.sharers, previous: previousTotals.sharers },
      caption: teamSize !== undefined ? (
        <>
          <Box component="span" sx={{ fontWeight: 700, color: 'var(--color-text-strong)' }}>{formatPercent(teamShare)}</Box>
          {` of ${formatInt(teamSize)} team ${teamSize === 1 ? 'agent' : 'agents'}`}
          <br />
          {linksPerSharerText}
        </>
      ) : linksPerSharerText,
      info: teamSize !== undefined
        ? 'Share of the team — active agents down the reporting line, the leader included — who shared at least one link in this period.'
        : undefined,
      spark: { values: series.map((p) => p.sharers), color: palette.links },
      periodLabel,
    },
    ...(hideProjects ? [] : [{
      label: 'Projects shared',
      value: formatInt(totals.projects),
      delta: { kind: 'count' as const, current: totals.projects, previous: previousTotals.projects },
      caption: topProject && topProject.links > 0 ? `Most shared: ${topProject.projectName}` : '—',
      periodLabel,
    }]),
    {
      label: 'Link opens',
      value: formatInt(totals.opens),
      caption: opensPerLink !== null ? `${opensPerLink.toFixed(1)} opens per link` : '—',
      info: 'Landing-page opens, all-time, on the links shared in this period. Opens are kept as a running total per link, so they have no daily breakdown.',
      accent: palette.opens,
      periodLabel,
    },
    {
      label: 'Verification completion',
      value: formatPercent(completion),
      delta: { kind: 'rate', current: completion, previous: previousCompletion },
      caption: `${formatInt(totals.verificationsStarted)} started · ${formatInt(totals.verificationsStarted - totals.verificationsCompleted)} didn't finish`,
      info: 'Share of buyers who asked for a WhatsApp code and then entered it. A low rate points to friction in the OTP step.',
      periodLabel,
    },
  ];

  return (
    <Grid container spacing={2}>
      {tiles.map((tile) => (
        // `lg` with no size shares the row equally, so five tiles fill it as six do.
        <Grid item xs={12} sm={6} md={4} lg key={tile.label}>
          <KpiTile {...tile} />
        </Grid>
      ))}
    </Grid>
  );
};

export default ShareLinksKpiRow;
